import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import type { SearchApi, SearchResultPage, SearchWindow } from '@bloo-team/api-client'
import { isFailedState, isTerminal, SearchQueryError } from '@bloo-team/api-client'
import { searchApiFor, useSearchFields } from './search'
import { isDemoSession } from './demo'
import { loadTimesliceForm, pickTimeslice, querySpanMs, saveTimesliceForm, timesliceFormKey } from './timeline'
import {
  costGateKey,
  countQuery,
  entityPredicate,
  entitySpecKey,
  entityTime,
  isUnresolvedColumn,
  loadCostGate,
  primaryFields,
  readStreamCounts,
  resolveFields,
  rowsQuery,
  timelineQuery,
  windowOf,
  type EntitySearchParams,
  type EntityTime,
  type StreamCount,
} from './entity-search'

/**
 * The entity search's jobs, wired to a component's lifetime.
 *
 * Shape (docs/entity-search-requirements.md §Workflow):
 *
 *   cost   realtimecost on the count query — gate above the scope threshold
 *   count  ONE `stream=* … | groupby Stream` job -> per-stream hit badges
 *   rows   one `stream=X … | limit 500` job per stream WITH hits, 4 at a time
 *   hist   one split-timeline job over `stream=*`, queued behind the rows
 *
 * Unlike useSearchJob this is promise-shaped, not react-query-shaped: N
 * jobs whose lifetimes are all "this search" are simplest as one async run
 * with one AbortController. Every running task is revoked on abort — a
 * re-run, a window change, or unmount — because an abandoned job keeps
 * running on the cluster otherwise.
 */

export type EntityPhase = 'idle' | 'costing' | 'gated' | 'counting' | 'fetching' | 'done' | 'failed'

export interface StreamJob {
  stream: string
  count: number
  phase: 'queued' | 'running' | 'done' | 'failed'
  taskId: string | null
  page: SearchResultPage | null
  pageNo: number
  error: string | null
}

export interface EntitySearchState {
  phase: EntityPhase
  /** The predicate that ran, for "open in Search" and the header. */
  predicate: string | null
  fields: string[]
  /** Fields pruned after an UNRESOLVED_COLUMN answer — shown, never silent. */
  droppedFields: string[]
  /** realtimecost of the count query (the full-scope scan). */
  cost: number | null
  costGate: number
  counts: StreamCount[]
  streams: Record<string, StreamJob>
  /** Split-timeline rows + column names, or null when unavailable. */
  timeline: { page: SearchResultPage; sliceMs: number; timeField: string } | null
  error: string | null
}

const IDLE: EntitySearchState = {
  phase: 'idle', predicate: null, fields: [], droppedFields: [], cost: null,
  costGate: 0, counts: [], streams: {}, timeline: null, error: null,
}

export const PARALLEL_JOBS = 4
const POLL_MS = 1000
const ROW_PAGE_SIZE = 100

class Aborted extends Error {
  constructor() {
    super('aborted')
    this.name = 'Aborted'
  }
}

function sleep(ms: number, signal: AbortSignal): Promise<void> {
  return new Promise((resolve, reject) => {
    if (signal.aborted) return reject(new Aborted())
    const t = setTimeout(() => {
      signal.removeEventListener('abort', onAbort)
      resolve()
    }, ms)
    const onAbort = () => {
      clearTimeout(t)
      reject(new Aborted())
    }
    signal.addEventListener('abort', onAbort, { once: true })
  })
}

interface Scope {
  clusterId: string
  scopeId: string
}

/**
 * invoke -> poll -> first result page, as one promise. Revokes on abort
 * while the task is still running; a finished task's stored result needs
 * no revoke. Throws SearchQueryError on rejection, Error on a failed task
 * or a `failed` result page (the SQL-style late failure, which is also how
 * UNRESOLVED_COLUMN arrives on the pipe dialect).
 */
export async function runToCompletion(
  api: SearchApi,
  scope: Scope,
  query: string,
  window: SearchWindow | undefined,
  signal: AbortSignal,
  pageSize: number,
): Promise<{ taskId: string; page: SearchResultPage }> {
  if (signal.aborted) throw new Aborted()
  const taskId = await api.invoke({ ...scope, query, dialect: 'dql', window })
  try {
    for (;;) {
      await sleep(POLL_MS, signal)
      const st = await api.state({ ...scope, taskId })
      const state = st.task_state
      if (isTerminal(state)) {
        if (isFailedState(state)) throw new Error(`the engine reported ${state}`)
        break
      }
    }
  } catch (e) {
    if (e instanceof Aborted) void api.revoke({ ...scope, taskId }).catch(() => {})
    throw e
  }
  if (signal.aborted) throw new Aborted()
  const page = await api.result({ ...scope, taskId, pageNo: 1, pageSize })
  if (page.failed) throw new Error(page.message ?? 'the query failed to execute')
  return { taskId, page }
}

/** Run `fn` over `items` with at most `limit` in flight; order of completion is free. */
async function pool<T>(items: readonly T[], limit: number, fn: (item: T) => Promise<void>): Promise<void> {
  let next = 0
  const worker = async () => {
    while (next < items.length) {
      const item = items[next++]!
      await fn(item)
    }
  }
  await Promise.all(Array.from({ length: Math.min(limit, items.length) }, worker))
}

export interface UseEntitySearch extends EntitySearchState {
  /** Proceed past the cost gate. */
  confirm: () => void
  /** Re-read one stream's stored result at another page. */
  setStreamPage: (stream: string, pageNo: number) => void
  cancel: () => void
}

export function useEntitySearch({
  clusterId,
  scopeId,
  params,
}: Scope & { params: EntitySearchParams | null }): UseEntitySearch {
  const isDemo = isDemoSession()
  const api = searchApiFor(isDemo)
  const vocab = useSearchFields({ clusterId, scopeId })
  const vocabulary = useMemo(() => {
    if (!vocab.data) return null
    const all = new Set<string>()
    for (const fields of Object.values(vocab.data)) for (const f of fields) all.add(f)
    return all
  }, [vocab.data])

  const [state, setState] = useState<EntitySearchState>(IDLE)
  const ctrl = useRef<AbortController | null>(null)
  const specKey = params ? entitySpecKey(params) : null
  const [confirmedKey, setConfirmedKey] = useState<string | null>(null)
  const tsKey = timesliceFormKey(clusterId, scopeId)

  const cancel = useCallback(() => {
    ctrl.current?.abort()
    ctrl.current = null
  }, [])

  // Abort on unmount.
  useEffect(() => cancel, [cancel])

  useEffect(() => {
    if (!params || !specKey) {
      cancel()
      return
    }
    // The vocabulary is a pruning aid, not a prerequisite: run without it
    // rather than wait on a 15-minute-cached call that may already be here.
    if (vocab.isPending && !vocab.data && !vocab.isError) return

    cancel()
    const ac = new AbortController()
    ctrl.current = ac
    const signal = ac.signal
    const scope = { clusterId, scopeId }
    const time: EntityTime = entityTime(params)
    const window = windowOf(time)
    const kind = params.type
    const gate = loadCostGate(costGateKey(clusterId, scopeId))
    const confirmed = confirmedKey === specKey

    let fields = resolveFields(kind, vocabulary)
    let pred = entityPredicate(kind, params.value, fields, !!params.raw)

    const patch = (p: Partial<EntitySearchState> | ((s: EntitySearchState) => EntitySearchState)) => {
      if (signal.aborted) return
      setState((s) => (typeof p === 'function' ? p(s) : { ...s, ...p }))
    }
    const patchStream = (stream: string, p: Partial<StreamJob>) =>
      patch((s) => ({ ...s, streams: { ...s.streams, [stream]: { ...s.streams[stream]!, ...p } } }))

    const run = async () => {
      patch({ ...IDLE, phase: 'costing', predicate: pred, fields, costGate: gate })
      /* --- cost gate ------------------------------------------------- */
      const cq = countQuery(pred, time)
      let cost: number | null
      try {
        cost = await api.cost({ ...scope, query: cq, dialect: 'dql', window })
      } catch {
        cost = null
      }
      if (signal.aborted) return
      if (cost !== null && cost > gate && !confirmed) {
        patch({ phase: 'gated', cost })
        return
      }
      patch({ phase: 'counting', cost })

      /* --- count job, with the UNRESOLVED_COLUMN retry ---------------- */
      let countPage: SearchResultPage
      try {
        countPage = (await runToCompletion(api, scope, cq, window, signal, 2000)).page
      } catch (e) {
        if (e instanceof Aborted) return
        const msg = e instanceof Error ? e.message : String(e)
        const primary = primaryFields(kind)
        if (!isUnresolvedColumn(msg) || primary.length === fields.length) {
          patch({ phase: 'failed', error: msg })
          return
        }
        // A declared-but-empty column sank the job: retry once on the
        // primary roles and say which fields were dropped.
        const dropped = fields.filter((f) => !primary.includes(f))
        fields = primary
        pred = entityPredicate(kind, params.value, fields, !!params.raw)
        patch({ predicate: pred, fields, droppedFields: dropped })
        try {
          countPage = (await runToCompletion(api, scope, countQuery(pred, time), window, signal, 2000)).page
        } catch (e2) {
          if (e2 instanceof Aborted) return
          patch({ phase: 'failed', error: e2 instanceof Error ? e2.message : String(e2) })
          return
        }
      }
      const counts = readStreamCounts(countPage.fields, countPage.rows)
      const streams: Record<string, StreamJob> = {}
      for (const c of counts) {
        streams[c.stream] = { stream: c.stream, count: c.count, phase: 'queued', taskId: null, page: null, pageNo: 1, error: null }
      }
      patch({ phase: counts.length ? 'fetching' : 'done', counts, streams })
      if (!counts.length) return

      /* --- rows per stream + the histogram, through one pool ---------- */
      const rowsJob = async (c: StreamCount) => {
        patchStream(c.stream, { phase: 'running' })
        try {
          const { taskId, page } = await runToCompletion(api, scope, rowsQuery(c.stream, pred, time), window, signal, ROW_PAGE_SIZE)
          patchStream(c.stream, { phase: 'done', taskId, page })
        } catch (e) {
          if (e instanceof Aborted) return
          patchStream(c.stream, { phase: 'failed', error: e instanceof Error ? e.message : String(e) })
        }
      }
      const histJob = async () => {
        if (loadTimesliceForm(`${tsKey}.split`) === 'none') return
        const stored = loadTimesliceForm(tsKey)
        if (stored === 'none') return
        const span = querySpanMs(cq, window)
        if (!span) return
        const slice = pickTimeslice(span)
        const forms = stored === 'cnamtime' ? (['cnamtime'] as const) : stored === 'timeslice' ? (['timeslice'] as const) : (['timeslice', 'cnamtime'] as const)
        for (const form of forms) {
          const tq = timelineQuery(pred, time, slice.label, form)
          if (!tq) return
          try {
            const { page } = await runToCompletion(api, scope, tq, window, signal, 2000)
            if (stored === null) saveTimesliceForm(tsKey, form)
            patch({ timeline: { page, sliceMs: slice.ms, timeField: form === 'timeslice' ? 'timeslice' : 'CNAMTime' } })
            return
          } catch (e) {
            if (e instanceof Aborted) return
            // A synchronous rejection is grammar; try the other form, then
            // remember the split as unsupported. A late failure is data.
            if (!(e instanceof SearchQueryError)) return
          }
        }
        if (stored !== null) saveTimesliceForm(`${tsKey}.split`, 'none')
      }
      const work: Array<() => Promise<void>> = [...counts.map((c) => () => rowsJob(c)), histJob]
      await pool(work, PARALLEL_JOBS, (w) => w())
      patch({ phase: 'done' })
    }

    void run().catch((e: unknown) => {
      if (e instanceof Aborted) return
      patch({ phase: 'failed', error: e instanceof Error ? e.message : String(e) })
    })
    // `params` is fully represented by specKey; api identity follows isDemo.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [specKey, confirmedKey, clusterId, scopeId, isDemo, vocabulary, vocab.isPending, vocab.isError, cancel, tsKey])

  const confirm = useCallback(() => {
    if (specKey) setConfirmedKey(specKey)
  }, [specKey])

  const setStreamPage = useCallback(
    (stream: string, pageNo: number) => {
      const s = state.streams[stream]
      if (!s?.taskId) return
      const scope = { clusterId, scopeId }
      const taskId = s.taskId
      setState((prev) => ({ ...prev, streams: { ...prev.streams, [stream]: { ...prev.streams[stream]!, pageNo } } }))
      void api
        .result({ ...scope, taskId, pageNo, pageSize: ROW_PAGE_SIZE })
        .then((page) => {
          setState((prev) => {
            const cur = prev.streams[stream]
            if (!cur || cur.taskId !== taskId || cur.pageNo !== pageNo) return prev
            return { ...prev, streams: { ...prev.streams, [stream]: { ...cur, page } } }
          })
        })
        .catch(() => {})
    },
    [api, clusterId, scopeId, state.streams],
  )

  // No params, no search: derived, not reset in an effect.
  return { ...(params ? state : IDLE), confirm, setStreamPage, cancel }
}
